import {
  type AnyAction,
  combineReducers,
  configureStore,
} from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  createTransform,
  persistReducer,
  persistStore,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {
  createStateSyncMiddleware,
  initMessageListener,
} from 'redux-state-sync';

import { api, eventApi, paymentApi } from '@/app/providers/api';
import { authReducer } from '@/features/auth/store/auth';
import { cmsApi } from '@/features/chat/services/cms';
import { chatReducer } from '@/features/chat/store/chat';
import { planReducer } from '@/features/payment/store/plan';
import { settingsReducer } from '@/features/settings/store/settings';

const chatTransform = createTransform(
  (inboundState: ReturnType<typeof chatReducer>) => {
    return {
      ...inboundState,
      messages: [],
      isAnswering: false,
      preSearch: {
        isLoading: false,
        data: inboundState.preSearch.data,
        error: null,
        selectedPersonId: inboundState.preSearch.selectedPersonId,
        showPersonList: inboundState.preSearch.showPersonList,
      },
    };
  },
  (outboundState) => outboundState,
  { whitelist: ['chat'] },
);

const appReducer = combineReducers({
  auth: authReducer,
  settings: settingsReducer,
  chat: chatReducer,
  [api.reducerPath]: api.reducer,
  [eventApi.reducerPath]: eventApi.reducer,
  [paymentApi.reducerPath]: paymentApi.reducer,
  [cmsApi.reducerPath]: cmsApi.reducer,
  plan: planReducer,
});

const rootReducer = (
  state: ReturnType<typeof appReducer> | undefined,
  action: AnyAction,
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
) => {
  if (action.type === 'auth/logOut') {
    // eslint-disable-next-line no-console
    storage.removeItem('persist:root').catch(console.error);
    localStorage.removeItem('deepsearch_input_draft');
    localStorage.removeItem('searchText');

    const nextState = appReducer(undefined, { type: undefined });

    return {
      ...nextState,
      [cmsApi.reducerPath]:
        state?.[cmsApi.reducerPath] ?? nextState[cmsApi.reducerPath],
    };
  }

  return appReducer(state, action);
};

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['auth', 'chat'],
  transforms: [chatTransform],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
const middlewareHandler = (getDefaultMiddleware: any) => {
  const middlewareList = [
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    ...getDefaultMiddleware({
      serializableCheck: {
        // NOTE: Ignore all the action types redux-resist dispatches to prevent non-serializable value error
        // https://redux-toolkit.js.org/usage/usage-guide#use-with-redux-persist
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
    createStateSyncMiddleware({
      whitelist: ['auth/logOut', 'auth/setUser'],
    }),
    api.middleware,
    eventApi.middleware,
    paymentApi.middleware,
    cmsApi.middleware,
  ];

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return middlewareList;
};

export const store = configureStore({
  reducer: persistedReducer,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  middleware: (getDefaultMiddleware) => middlewareHandler(getDefaultMiddleware),
});

initMessageListener(store);
setupListeners(store.dispatch);

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
